
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { ContractType } from "@/lib/types";
import { useTranslation } from "react-i18next";

interface ContractTypesListProps {
  contractTypes?: ContractType[];
  isLoading: boolean;
  error: unknown;
  onDeleteClick: (contractType: ContractType) => void;
}

export function ContractTypesList({ 
  contractTypes, 
  isLoading, 
  error, 
  onDeleteClick 
}: ContractTypesListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border bg-card shadow-sm p-6 space-y-4">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex justify-end">
              <Skeleton className="h-9 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load contract types: {(error as Error).message}
        </AlertDescription>
      </Alert>
    );
  }

  if (contractTypes && contractTypes.length > 0) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {contractTypes.map((type) => (
          <div
            key={type.id}
            className="rounded-lg border bg-card shadow-sm transition-all hover:shadow-md animate-fade-in"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold">{type.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {type.description || "No description provided"}
              </p>
              <div className="flex justify-end mt-4 gap-2">
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => onDeleteClick(type)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/contract-types/${type.id}`}>Manage</Link>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <p className="text-muted-foreground">No contract types found. Create your first one!</p>
    </div>
  );
}
